import React from 'react';
import * as Sentry from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from 'react-router';
import { Constant } from './consts';
import json from '../../package.json';
import { ErrorsTag, InfoTag } from './Api/Api.model';

const PDF_DIST_ERROR_MESSAGE =
  "t.at is not a function. (In 't.at(-1)', 't.at' is undefined";
const LIVECHAT_ERROR_MESSAGE = 'e.call';

const initSentry = () => {
  const productionTraceSampler = 0.7;
  return process.env.REACT_APP_SENTRY_KEY
    ? Sentry.init({
        dsn: process.env.REACT_APP_SENTRY_KEY,
        release: `BEST Online - ${json.version}`,
        environment: process.env.REACT_APP_SENTRY_ENVIRONMENT,
        maxBreadcrumbs: 30,
        beforeBreadcrumb: (breadcrumb) => {
          const isExternalRequest =
            breadcrumb.category &&
            ['xhr', 'fetch'].includes(breadcrumb.category) &&
            ['google', 'cookiebot'].some((keyword) =>
              breadcrumb.data?.url?.includes(keyword)
            );
          if (isExternalRequest) {
            return null;
          }

          return breadcrumb;
        },
        beforeSend: (event, hint) => {
          const originalException = hint?.originalException as any;

          // technical break as info
          const technicalBreak = event.breadcrumbs?.some(
            (breadcrumb) =>
              breadcrumb.category === 'navigation' &&
              breadcrumb.data?.to?.includes('/technical-break')
          );
          if (technicalBreak) {
            event.tags = {
              ...event.tags,
              info_message: InfoTag.TechnicalBreak
            };
            event.level = 'info';
            return event;
          }

          // expired token filtered
          if (event.tags?.error_message === ErrorsTag.TokenExpired) {
            return null;
          }

          // livechat error as info
          const isLiveChatError =
            originalException &&
            originalException.message &&
            originalException.message
              .toString()
              .includes(LIVECHAT_ERROR_MESSAGE) &&
            originalException.fileName &&
            originalException.fileName.toString().includes('livechat');

          if (isLiveChatError) {
            event.tags = {
              ...event.tags,
              info_message: InfoTag.LiveChat
            };
            event.level = 'info';
            return event;
          }

          // pdf-dist as info
          const isPdfDistError =
            originalException &&
            originalException.toString().includes(PDF_DIST_ERROR_MESSAGE);

          if (isPdfDistError) {
            event.tags = {
              ...event.tags,
              info_message: InfoTag.PdfDist
            };
            event.level = 'info';
            return event;
          }
          return event;
        },

        tracesSampler: () => {
          if (process.env.REACT_APP_SENTRY_ENVIRONMENT === 'prod') {
            return productionTraceSampler;
          }
          return Constant.one;
        },
        sendDefaultPii: true,
        integrations: [
          Sentry.reactRouterV7BrowserTracingIntegration({
            useEffect: React.useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes
          })
        ]
      })
    : null;
};

export default initSentry;
